import { Box, Button, Grid2, Skeleton, Stack, Typography } from '@mui/material'
import { createRef, useImperativeHandle, useRef } from 'react'
import { Icons, ItemImageArea } from 'utoniq-ui'

import {
  ItemSerialCountDetailFragment,
  Maybe,
  MultiItemSerialDetailFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, BottomSafeArea } from '@/components/layouts'
import {
  FullscreenModal,
  ScrollFetcher,
} from '@/components/organisms/functionals'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'
import { encodeSkuId } from '@/core/sku'
import { CanvasConfetti, CanvasConfettiRef } from '@/lib/canvasConfetti'

type GetItemMultiCompletionModalProps = {
  open: boolean
  loading: boolean
  items?: Maybe<MultiItemSerialDetailFragment['items']>
}

type ItemMultiCompletionModalRef = {
  playConfetti: () => void
}

export const itemMultiCompletionModalRef =
  createRef<ItemMultiCompletionModalRef>()

export const GetItemMultiCompletionModal: Cell<GetItemMultiCompletionModalProps> =
  ({ items, loading, open }) => {
    const { t } = useTranslation('qrCodeGetItemMulti')
    const canvasConfettiRef = useRef<CanvasConfettiRef>(null)

    useImperativeHandle(
      itemMultiCompletionModalRef,
      () => ({
        playConfetti() {
          canvasConfettiRef.current?.start()
        },
      }),
      []
    )

    return (
      <FullscreenModal open={open} onClose={() => {}}>
        <BaseLayout
          header={{
            title: t('getItemMultiCompleteTitle', 'アイテムを獲得しました'),
            sx: { zIndex: theme => theme.zIndex.modal + 3 },
          }}
          customNavigation={<FixedButtonCell />}
          sx={{ backgroundColor: 'background.default' }}
        >
          <ScrollFetcher
            nodes={items?.map(itemSerial => (
              <ItemBox itemSerial={itemSerial} />
            ))}
            loading={loading}
            hasNextPage={false}
            onFetchMore={async () => {}}
            component={Grid2}
            componentProps={{
              container: true,
              spacing: 3,
            }}
            itemComponent={Grid2}
            itemComponentProps={{
              size: { xs: 4, md: 3 },
              sx: { textAlign: 'center' },
            }}
            loadingComponent={
              <Grid2 container spacing={3}>
                {Array.from(Array(12).keys()).map(key => (
                  <Grid2 size={{ xs: 4, md: 3 }} key={key}>
                    <Skeleton
                      variant="rectangular"
                      sx={{ width: '100%', height: 166 }}
                    />
                  </Grid2>
                ))}
              </Grid2>
            }
          />
        </BaseLayout>
        <CanvasConfetti
          ref={canvasConfettiRef}
          sx={{ maxWidth: 600, margin: '0 auto', right: 0 }}
        />
      </FullscreenModal>
    )
  }

type ItemBoxProps = {
  itemSerial: ItemSerialCountDetailFragment
}
const ItemBox: Cell<ItemBoxProps> = ({ itemSerial }) => {
  const { generateQueryPath, routerPages } = useRouter()
  const { item, count, sku } = itemSerial

  return (
    <Stack
      flex={1}
      sx={{ position: 'relative', zIndex: 10, py: 1 }}
      alignItems="center"
      justifyContent="center"
    >
      <Box
        sx={{
          position: 'relative',
          width: 'fit-content',
          img: {
            boxShadow:
              item?.rarity?.id === 'superrare' ||
              item?.rarity?.id === 'ultrarare'
                ? '0px 0px 40px 0px rgba(250, 255, 0, 0.6)'
                : 'none',
          },
        }}
      >
        <ItemImageArea
          src={item?.thumbnail.front?.uri ?? ''}
          href={generateQueryPath(
            routerPages.OwnedItemDetail,
            { itemId: item.id },
            { skuId: encodeSkuId(sku?.id ?? '') }
          )}
        />
        {count > 1 && (
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              zIndex: 11,
              bgcolor: 'secondary.main',
              borderRadius: '2px',
              px: '2px',
            }}
          >
            <Icons.OwnedItems sx={{ color: 'white', width: 12, height: 12 }} />
            <Typography variant="subtitle2" color="white">
              ×{count}
            </Typography>
          </Stack>
        )}
      </Box>
    </Stack>
  )
}

type FixedButtonCellProps = {}
const FixedButtonCell: Cell<FixedButtonCellProps> = () => {
  const { t } = useTranslation('qrCodeGetItemMulti')
  const { routerPages } = useRouter()

  return (
    <CellFrame>
      <BottomSafeArea sx={{ bgcolor: 'grey.50' }}>
        <Stack
          sx={{ py: 4, bgcolor: 'grey.50' }}
          spacing={2}
          alignItems="center"
        >
          <Button
            variant="outlined"
            href={routerPages.MyOwnedItems}
            sx={{ position: 'relative', zIndex: 10 }}
          >
            {t('checkItems', 'アイテムを確認する')}
          </Button>
          <Button
            variant="text"
            color="primary"
            href={routerPages.Top}
            sx={{ position: 'relative', zIndex: 10 }}
          >
            {t('goToTop')}
          </Button>
        </Stack>
      </BottomSafeArea>
    </CellFrame>
  )
}
